'use client';

import { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { createClient } from '@/lib/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import type { DumpsterAction } from './types';
import { getDumpsterDetails } from './dumpsterLookup';

interface DumpsterConfirmDialogProps {
  action: DumpsterAction | null;
  onClose: () => void;
}

export default function DumpsterConfirmDialog({ action, onClose }: DumpsterConfirmDialogProps) {
  const [submitting, setSubmitting] = useState(false);
  const supabase = createClient();
  const { currentUser } = useAuth();

  // Reset submitting state when a new action opens OR when dialog closes
  useEffect(() => {
    setSubmitting(false);
  }, [action?.job.id, action?.type]);

  // Close on Escape
  useEffect(() => {
    if (!action) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !submitting) onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [action, submitting, onClose]);

  if (!action) return null;

  const details = getDumpsterDetails(action.job.job_id);
  const dumpsterVendor = details.vendor;
  const dumpsterSize = details.size;
  const fromStatusLabel = action.type === 'drop' ? 'Drop Pending' : 'On Site';
  const toStatusLabel = action.type === 'drop' ? 'On Site' : 'Removed';

  const iconBg = action.type === 'drop' ? '#dcfce7' : '#e5e7eb';
  const iconColor = action.type === 'drop' ? '#166534' : '#374151';
  const confirmBg = action.type === 'drop' ? '#16a34a' : '#374151';
  const confirmHoverBg = action.type === 'drop' ? '#15803d' : '#1f2937';

  const handleConfirm = async () => {
    if (!action) return;
    setSubmitting(true);

    const newStatus = action.type === 'drop' ? 'Placed' : 'Removed';
    const actorName = `${currentUser?.first_name ?? 'Unknown'} ${currentUser?.last_name ?? ''}`.trim();

    try {
      const { error } = await supabase
        .from('logistics_jobs')
        .update({
          dumpster_status: newStatus,
          updated_at: new Date().toISOString(),
        })
        .eq('id', action.job.id);

      if (error) throw error;

      toast.success(
        action.type === 'drop'
          ? `${action.job.job_id} dumpster marked on-site`
          : `${action.job.job_id} dumpster picked up`,
        { duration: 3500, icon: action.type === 'drop' ? '📦' : '✓' }
      );

      onClose();
    } catch (err) {
      console.error('Failed to update dumpster status:', err);
      const message = err instanceof Error ? err.message : 'Unknown error';
      toast.error(`Update failed: ${message}`, { duration: 6000 });
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/40 backdrop-blur-sm z-[100] flex items-center justify-center p-4"
      onClick={submitting ? undefined : onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="dumpster-confirm-title"
    >
      <div
        className="bg-white rounded-2xl shadow-2xl w-full max-w-md"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-gray-100">
          <div className="flex items-start gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
              style={{ background: iconBg }}
            >
              <svg
                className="w-5 h-5"
                style={{ color: iconColor }}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                aria-hidden="true"
              >
                {action.type === 'drop' ? (
                  <>
                    <path d="M3 6h18M6 6v14a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V6" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10 10v8M14 10v8" strokeLinecap="round"/>
                    <path d="M9 3h6v3H9z" strokeLinecap="round" strokeLinejoin="round"/>
                  </>
                ) : (
                  <path d="M20 6 9 17l-5-5" strokeLinecap="round" strokeLinejoin="round"/>
                )}
              </svg>
            </div>
            <div className="flex-1">
              <h3 id="dumpster-confirm-title" className="text-lg font-bold text-[#1C2E5E]">
                {action.type === 'drop' ? 'Confirm Dumpster Drop' : 'Confirm Dumpster Pickup'}
              </h3>
              <p className="text-[12px] text-gray-500 mt-0.5">
                {action.type === 'drop' ?'Mark the dumpster as on-site and ready for the install.' :'Mark the dumpster as picked up. The job will finalize this step.'}
              </p>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="p-6">
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-3 space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Job</span>
              <span className="font-mono font-bold text-[13px] text-[#1C2E5E]">{action.job.job_id}</span>
            </div>
            <div className="text-[12px] text-gray-700">{action.job.address}</div>
            <div className="flex items-center gap-2 pt-1">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">Dumpster</span>
              <span className="text-[11px] font-semibold text-gray-700">
                {dumpsterVendor} · {dumpsterSize}
              </span>
            </div>
          </div>

          <div className="mt-4 flex items-center gap-2 text-[12px]">
            <span className="px-2 py-0.5 rounded-full font-semibold bg-gray-100 text-gray-600">
              {fromStatusLabel}
            </span>
            <svg className="w-4 h-4 text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
              <path d="m9 18 6-6-6-6" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            <span
              className="px-2 py-0.5 rounded-full font-semibold"
              style={{
                background: action.type === 'drop' ? '#dcfce7' : '#e5e7eb',
                color: action.type === 'drop' ? '#166534' : '#374151',
              }}
            >
              {toStatusLabel}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 p-6 border-t border-gray-100">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold text-gray-600 hover:bg-gray-100 transition-colors disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={submitting}
            className="px-4 py-2 rounded-lg text-[13px] font-semibold text-white transition-colors inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: submitting ? confirmBg : confirmBg }}
            onMouseEnter={e => { if (!submitting) (e.currentTarget as HTMLButtonElement).style.background = confirmHoverBg; }}
            onMouseLeave={e => { (e.currentTarget as HTMLButtonElement).style.background = confirmBg; }}
          >
            {submitting && (
              <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeOpacity="0.25" strokeWidth="3"/>
                <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round"/>
              </svg>
            )}
            {submitting ? 'Updating…' : (action.type === 'drop' ? 'Confirm Drop' : 'Confirm Pickup')}
          </button>
        </div>
      </div>
    </div>
  );
}
